<template>
  <div>
    <div v-if="step === 1">
      <SelectJobTypeForm
        @selectedTemplate="handleSelectTemplate"
        @selectedFromScratch="step = 2"
        :isLoading="isLoading"
        :templates="data.templates"
      />
    </div>
    <div v-if="step === 2">
      <form @submit.prevent="handleSubmit" novalidate>
        <JobForm :data="data" :errors="errors" />

        <JobSubmitSection
          :isLoading="isLoading"
          :metadata="data.metadata"
          @update:status="data.metadata.status = $event"
          @updateAction="action = $event"
        />

        <TemplatesOverwriteModal
          v-if="isOverWriteTemplateModalOpen"
          :is-loading="isLoading"
          @overwrite="handleOverwriteTemplate"
          @cancel="isOverWriteTemplateModalOpen = false"
        />
      </form>
    </div>
  </div>
</template>

<script setup>
  import { nextTick, ref } from 'vue';

  import { useToast } from 'vue-toastification';
  import { __ } from '@/helpers';
  import { JobForm } from './partials';
  import { useForm } from '../../../utils';
  import { JobSubmitSection } from './components';
  import SelectJobTypeForm from './components/SelectJobTypeForm.vue';
  import axios from 'axios';
  import TemplatesOverwriteModal from './templates/components/TemplatesOverwriteModal.vue';

  const props = defineProps({
    name: String,
    data: {
      type: Object,
      required: true
    }
  });

  const toast = useToast();
  const action = ref('');
  const isOverWriteTemplateModalOpen = ref(false);
  //go straight to job creation if no template exists + double check with the metadata value
  const step = !props.data.templates || props.data.templates.length === 0 ? ref(2) : ref(1);
        step.value = !props.data.metadata.active_job_templates ? 2 : step.value;

  const {
    formData: data,
    isLoading,
    updateFormData,
    resetForm,
    submitForm,
    errors
  } = useForm(props.data, {
      method: 'post',
      url: route('redesign.company.jobs.store')
    });

  const scrollToFirstError = async () => {
    await nextTick();
    const firstErr = document.querySelector('.text-error');
    if (firstErr) {
      window.scrollTo({
        top: firstErr.getBoundingClientRect().top + window.scrollY - 160,
        behavior: 'smooth'
      });
    }
  };

  const handleUnexpectedError = async (error) => {
    await scrollToFirstError();
    toast.error(error.response?.data?.message || __('Something went wrong.'));
  };

  const redirectToJobShow = (submitResponse) => {
    toast.success(submitResponse.message);

    setTimeout(() => {
      window.location.replace(
          route('redesign.jobs.show', {
            company: submitResponse.company,
            job: submitResponse.job
          })
      );
    }, 1200);
  };

  const redirectToTemplatesListing = (response) => {
    toast.success(response.data?.message);
    setTimeout(() => {
      window.location.href = route('redesign.company.templates.index');
    }, 1200);
  };

  const handleSubmit = async () => {
    try {
      if (action.value === 'createTemplateFromJob') {
        await saveAsTemplate();
      } else {
        await submitJobForm();
      }
    } catch (error) {
      await handleUnexpectedError(error);
    }
  };

  const submitTemplate = () => {
    return axios.post(
        route('redesign.company.templates.save'),
        data.value
    );
  };

  const saveAsTemplate = async () => {
    try {
      isLoading.value = true;
      const response = await submitTemplate();
      redirectToTemplatesListing(response);
    } catch (error) {
      if (error.response?.status === 409) {
        isOverWriteTemplateModalOpen.value = true;
      } else {
        await handleUnexpectedError(error);
      }
    } finally {
      isLoading.value = false;
    }
  };

  const submitJobForm = async () => {
    data.value.metadata.action = action.value || null;

    const submitResponse = await submitForm();

    if (Object.keys(errors.value).length === 0) {
      redirectToJobShow(submitResponse);
    } else {
      await scrollToFirstError();
      toast.error(__('You have some errors in your form!'));
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      const response = await axios.post(
          route('redesign.company.jobs.create-from-template', template.template.id),
          data.value
      );
      Object.assign(data.value, response.data);
      toast.success(__('Template applied successfully!'));
      step.value = 2;
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong.'));
    }
  };

  const handleOverwriteTemplate = async () => {
    try {
      data.value.override = true;
      const response = await submitTemplate();
      redirectToTemplatesListing(response);
    } catch (error) {
      await handleUnexpectedError(error);
    } finally {
      isOverWriteTemplateModalOpen.value = false;
    }
  };
</script>
